// app/frontend/app/about-us/page.tsx
"use client"
import React from 'react';
import {
    Globe,
    Users,
    Target,
    Shield,
    Star,
    Award,
    Calendar,
    TrendingUp,
    Linkedin,
    Mail,
    Lightbulb,
} from 'lucide-react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

// Componente para cards alternados (mantido caso queira reutilizar)
const AlternatingCard = ({
    image,
    title,
    content,
    isRight = false,
    icon: Icon,
    bgColor = "black"
}: {
    image?: string;
    title: string;
    content: string;
    isRight?: boolean;
    icon?: React.ComponentType<any>;
    bgColor?: "white" | "black";
}) => {
    const textColor = bgColor === "white" ? "text-gray-900" : "text-white";
    const secondaryTextColor = bgColor === "white" ? "text-gray-700" : "text-gray-300";

    return (
        <div className={`flex flex-col lg:flex-row items-center gap-8 mb-16 ${isRight ? 'lg:flex-row-reverse' : ''}`}>
            {/* Imagem */}
            <div className="flex-1">
                <div className="relative group">
                    <div className="w-full h-80 rounded-3xl overflow-hidden shadow-2xl">
                        {image ? (
                            <img
                                src={image}
                                alt={title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-300 flex items-center justify-center">
                                {Icon && <Icon className="w-20 h-20 text-gray-600 opacity-80" />}
                            </div>
                        )}
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-10 rounded-3xl group-hover:bg-opacity-5 transition-all duration-300" />
                </div>
            </div>

            {/* Conteúdo */}
            <div className="flex-1">
                <div className="max-w-md mx-auto lg:mx-0">
                    <h3 className={`text-3xl font-bold mb-4 ${textColor}`}>{title}</h3>
                    <p className={`text-lg leading-relaxed ${secondaryTextColor}`}>{content}</p>
                </div>
            </div>
        </div>
    );
};

export default function AboutUsPage() {
    return (
        <div className="min-h-screen bg-white" id='about'>
            <Navbar />

            {/* Hero Section */}
            <section className="relative bg-black text-white py-32 px-4 overflow-hidden mt-10">
                <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-5"></div>
                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 bg-yellow-500 text-black px-6 py-3 rounded-full text-sm font-medium mb-8">
                        <Star className="w-5 h-5" />
                        About Media Localization Alliance
                    </div>
                    <h1 className="text-6xl md:text-7xl font-bold mb-8">
                        About<span className="text-yellow-500"> Us</span>
                    </h1>
                    <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        Pioneering the future of media localization through collaboration,
                        innovation, and global partnership.
                    </p>
                </div>
            </section>

            {/* About MLA Section */}
            <section className="relative py-24 px-4 bg-white">
                <div className="max-w-5xl mx-auto">

                    {/* Título */}
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-100">
                            About <span className="text-yellow-500">MLA</span>
                        </h2>
                    </div>

                    {/* Corpo do texto */}
                    <div className="space-y-14 text-lg leading-relaxed text-gray-700">

                        <div className="border-l-4 border-yellow-500 pl-6">
                            <p>
                                The Media Localization Alliance (MLA) is a free, global non-profit organization
                                designed to create an information hub where Trusted Partner Network (TPN) members
                                operating certified dubbing studios can access a collaborative network of nearly
                                50 studios worldwide, fostering deeper connections and growth opportunities.
                            </p>
                        </div>

                        <div className="border-l-4 border-gray-300 pl-6">
                            <p>
                                MLA members receive exclusive benefits including discounts on studio rentals,
                                idea exchange, project promotion, and participation in in-person meetings funded
                                by the organization. These gatherings happen at least twice a year during major
                                industry events such as NAB, MIPCOM or IBC. Updates about members and their work
                                are shared monthly on the MLA website and LinkedIn page.
                            </p>
                        </div>

                        <div className="border-l-4 border-gray-400 pl-6">
                            <p>
                                MLA will also serve as a showcase for emerging technologies that enhance
                                efficiency, reduce costs, and improve turnaround time. A network of
                                MPAA-compliant, TPN-certified studios strengthens trust and positions the group
                                as a powerful force in the global localization marketplace.
                            </p>
                        </div>

                    </div>
                </div>
            </section>



            {/* Objectives Section */}
            <section className="bg-gray-50 py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"><span className='text-yellow-500'>MLA</span> Objectives</h2>
                    </div>

                    <div className="bg-white rounded-3xl p-8 border border-gray-200 shadow-lg">
                        <div className="max-w-4xl mx-auto text-center">
                            <p className="text-gray-700 leading-relaxed text-xl mb-8">
                                The Media Localization Alliance (MLA) unites leading dubbing studios from around the world to bring stories to life across languages and cultures. With a presence in key countries renowned for dubbing, MLA fosters collaboration and excellence, ensuring high-quality localization, compliance with content securities best practices that resonates with global audiences.
                            </p>
                            <p className="text-gray-700 leading-relaxed text-xl font-semibold">
                                Together, we break barriers and connect creators with viewers worldwide.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section (restaurado) */}
            <section className="bg-white py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Member Benefits</h2>
                        <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                            Exclusive advantages designed to empower our network of studios
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                icon: Award,
                                title: "Studio Discounts",
                                description: "Exclusive discounts on rentals within our global network"
                            },
                            {
                                icon: Lightbulb,
                                title: "Idea Exchange",
                                description: "Collaborative platform for knowledge and project sharing"
                            },
                            {
                                icon: Calendar,
                                title: "Funded Meetings",
                                description: "Regular in-person meetings at major industry events"
                            },
                            {
                                icon: TrendingUp,
                                title: "Visibility",
                                description: "Featured promotions on MLA platforms and social media"
                            }
                        ].map((benefit, index) => (
                            <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-200 hover:border-yellow-500 transition-all duration-300">
                                <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <benefit.icon className="w-8 h-8 text-black" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                                <p className="text-gray-600">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Founders Section (images cover + fallback + moldura) */}
            <section className="bg-black py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-yellow-500 text-black px-6 py-3 rounded-full text-sm font-medium mb-6">
                            <Users className="w-5 h-5" />
                            Meet Our Team
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Founders</h2>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            The visionary leaders driving MLA's mission to transform media localization
                        </p>
                    </div>

                    {/* Founder 1 */}
                    <div className="flex flex-col lg:flex-row items-center gap-12 mb-20">
                        <div className="flex-1 flex justify-center">
                            <div className="relative">
                                <div className="w-[350px] h-[560px] rounded-3xl overflow-hidden shadow-2xl bg-gray-900">
                                    <img
                                        src="/anderson-ai.png"
                                        alt="Anderson - CEO & Founder"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                    <div className="hidden w-full h-full bg-gradient-to-br from-gray-100 to-gray-300 flex items-center justify-center">
                                        <Users className="w-20 h-20 text-gray-600" />
                                    </div>
                                </div>
                                <div className="absolute inset-0 border-2 border-yellow-500 border-opacity-40 rounded-3xl pointer-events-none shadow-lg" />
                            </div>
                        </div>
                        <div className="flex-1 text-white">
                            <h3 className="text-3xl font-bold mb-2">Anderson Rocha</h3>
                            <p className="text-yellow-500 text-xl font-semibold mb-4">CEO & Founder</p>
                            <p className="text-gray-300 text-lg leading-relaxed mb-6">
                                Visionary leader with extensive experience in media localization and business development. Anderson drives MLA's strategic direction and global partnerships, bringing innovative solutions to the dubbing industry.
                            </p>
                            <div className="flex justify-center lg:justify-start space-x-4">
                                <a
                                    href="mailto:anderson@alcateiaaudiovisual.com.br?subject=Interested%20in%20MLA"
                                    className="w-12 h-12 bg-gray-100 text-black rounded-xl flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-all duration-300"
                                    title="Email Anderson"
                                >
                                    <Mail className="w-5 h-5" />
                                </a>
                                <a
                                    href="https://linkedin.com/in/juan"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-gray-100 text-black rounded-xl flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-all duration-300"
                                    title="LinkedIn Juan"
                                >
                                    <Linkedin className="w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Founder 2 */}
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-12 mb-20">
                        <div className="flex-1 flex justify-center">
                            <div className="relative">
                                <div className="w-[350px] h-[560px] rounded-3xl overflow-hidden shadow-2xl bg-gray-900">
                                    <img
                                        src="/marcelo-ai.png"
                                        alt="Marcelo - CTO & Co-Founder"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                    <div className="hidden w-full h-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                                        <Users className="w-20 h-20 text-black" />
                                    </div>
                                </div>
                                <div className="absolute inset-0 border-2 border-yellow-500 border-opacity-40 rounded-3xl pointer-events-none shadow-lg" />
                            </div>
                        </div>
                        <div className="flex-1 text-white">
                            <h3 className="text-3xl font-bold mb-2">Marcelo Vidal</h3>
                            <p className="text-yellow-500 text-xl font-semibold mb-4">CEO & Founder</p>
                            <p className="text-gray-300 text-lg leading-relaxed mb-6">
                                Technology expert specializing in media workflows and digital innovation. Marcelo leads MLA's technical development and platform architecture, ensuring cutting-edge solutions for member studios.
                            </p>
                            <div className="flex justify-center lg:justify-start space-x-4">
                                <a
                                    href="mailto:marcelo@alcateiaaudiovisual.com.br?subject=Interested%20in%20MLA"
                                    className="w-12 h-12 bg-gray-100 text-black rounded-xl flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-all duration-300"
                                    title="Email Marcelo"
                                >
                                    <Mail className="w-5 h-5" />
                                </a>
                                <a
                                    href="https://linkedin.com/in/juan"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-gray-100 text-black rounded-xl flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-all duration-300"
                                    title="LinkedIn Juan"
                                >
                                    <Linkedin className="w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Founder 3 */}
                    <div className="flex flex-col lg:flex-row items-center gap-12">
                        <div className="flex-1 flex justify-center">
                            <div className="relative">
                                <div className="w-[350px] h-[560px] rounded-3xl overflow-hidden shadow-2xl bg-gray-900">
                                    <img
                                        src="/Juan-ai.png"
                                        alt="Juan - TPN"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                    <div className="hidden w-full h-full bg-gradient-to-br from-gray-100 to-gray-300 flex items-center justify-center">
                                        <Users className="w-20 h-20 text-gray-600" />
                                    </div>
                                </div>
                                <div className="absolute inset-0 border-2 border-yellow-500 border-opacity-40 rounded-3xl pointer-events-none shadow-lg" />
                            </div>
                        </div>
                        <div className="flex-1 text-white">
                            <h3 className="text-3xl font-bold mb-2">Juan Reyes</h3>
                            <p className="text-yellow-500 text-xl font-semibold mb-4">Executive Director</p>
                            <p className="text-gray-300 text-lg leading-relaxed mb-6">
                                President of Tech Align Group and Chair of the SMPTE Hollywood Section, with over 23 years of experience in content security and Media & Entertainment technology. He has contributed to major industry organizations such as TPN, SMPTE, the 8K Association, and HPA, helping develop technical standards and compliance programs. He currently serves as Executive Director of the 8K Association, leads the Interoperability Working Group at the UHD Alliance, and works as an advanced TPN assessor.                            </p>
                            <div className="flex justify-center lg:justify-start space-x-4">
                                <a
                                    href="https://linkedin.com/in/juan"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-gray-100 text-black rounded-xl flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-all duration-300"
                                    title="LinkedIn Juan"
                                >
                                    <Linkedin className="w-5 h-5" />
                                </a>
                                <a
                                    href="mailto:juan@mla.org?subject=Interested%20in%20MLA"
                                    className="w-12 h-12 bg-gray-100 text-black rounded-xl flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-all duration-300"
                                    title="Email Juan"
                                >
                                    <Mail className="w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
